import React from "react";
import clonedeep from "lodash.clonedeep";
// Limitless imports
import { useNotificationContext } from "@limitless/common/hooks";
import { useExpertOnboardingContext } from "../ExpertOnboardingContext";
import { useExpertOnboardingContentHook } from "../ExpertOnboardingContentHook";
import { useExpertOnboardingBrandLearningContentHook } from "../ExpertOnboardingBrandLearningContentHook";
import { INITIAL_QUIZ_STATE, quizReducer, UPDATE_QUIZ, LOADING, CLOSE_QUIZ, TRY_AGAIN, FINISH_QUIZ, showNoAttemptsLeft } from "./ExpertOnboardingQuizHelper";
const INITIAL_STATE = {
  template: null,
  sections: []
};

// This is for backward compatibility only, patching here instead of S3 for existing MCQs
const defaultSectionContent = t => ({
  "no-attempts-left-message": {
    sectionName: "no-attempts-left-message",
    authoringTitle: "NO ATTEMPTS LEFT MESSAGE",
    header: t("Sorry"),
    subHeader: t("You didn't pass the quiz. You don't have any attempts left"),
    buttonLabel: t("Close"),
    score: t("Your score:"),
    scoreSuffix: ""
  }
});
export const useExpertOnboardingQuizContentHook = t => {
  const [onboardingMcQuizContent, setOnboardingMcQuizContent] = React.useState(INITIAL_STATE);
  const [quizTryAgainSuccessContent, setQuizTryAgainSuccessContent] = React.useState({});
  const [wrongAnswers, setWrongAnswers] = React.useState([]);
  const [quizState, dispatch] = React.useReducer(quizReducer, INITIAL_QUIZ_STATE);
  const {
    addFailureNotification
  } = useNotificationContext();
  const {
    changeOnboardingInvitationStatus,
    updateQuizStatus,
    getOnboardingDetail,
    optInGroup,
    initiateBrandLearning
  } = useExpertOnboardingContext();
  const {
    fetchExpertOnboardingContent
  } = useExpertOnboardingContentHook();
  const [showBrandLearningPage, setShowBrandLearningPage] = React.useState(false);
  const {
    onboardingBrandLearningContent
  } = useExpertOnboardingBrandLearningContentHook();
  const [multipleChoiceQuizModalOpen, setMultipleChoiceQuizModalOpen] = React.useState(false);
  React.useEffect(() => {
    if (quizState.quizResult) {
      const {
        sections
      } = onboardingMcQuizContent;
      const quizCTASection = sections.find(item => item.sectionName === "quiz-cta");
      const questions = sections.filter(item => item.sectionName.includes("mc-quiz-question"));
      const correctAnswers = sections.filter(item => Reflect.getOwnPropertyDescriptor(item, "selectedAnswer") && item.selectedAnswer === item.correctAnswer);
      const scoreSuffix = `${correctAnswers.length}/${questions.length}`;
      if (quizState.quizResult === "pass") {
        getTryAgainSuccessContent(quizCTASection.subSections, "success-message", scoreSuffix);
      } else {
        if (showNoAttemptsLeft(onboardingMcQuizContent, quizState)) {
          getTryAgainSuccessContent(quizCTASection.subSections, "no-attempts-left-message", scoreSuffix);
        } else {
          const wrongAnswers = sections.filter(item => Reflect.getOwnPropertyDescriptor(item, "selectedAnswer") && item.selectedAnswer !== item.correctAnswer);
          const scoreSuffix = `${questions.length - wrongAnswers.length}/${questions.length}`;
          getTryAgainSuccessContent(quizCTASection.subSections, "try-again", scoreSuffix);
          setWrongAnswers(wrongAnswers);
        }
      }
    }
  }, [quizState.quizResult]);
  React.useEffect(() => {
    if (optInGroup !== null && optInGroup !== void 0 && optInGroup.mcqPageEnabled) {
      fetchQuizContent();
    }
  }, [optInGroup]);
  React.useEffect(() => {
    var _onboardingMcQuizCont;
    if (((_onboardingMcQuizCont = onboardingMcQuizContent.sections) === null || _onboardingMcQuizCont === void 0 ? void 0 : _onboardingMcQuizCont.length) > 0) {
      const sections = randomiseQuestion(clonedeep(onboardingMcQuizContent.sections));
      setOnboardingMcQuizContent({
        ...onboardingMcQuizContent,
        sections
      });
    }
  }, [multipleChoiceQuizModalOpen]);
  const fetchQuizContent = () => {
    fetchExpertOnboardingContent("multipleChoiceQuiz.json", optInGroup, quizContentSuccess, quizContentFailure);
  };
  const randomiseQuestion = filterSections => {
    const {
      randomise
    } = filterSections[filterSections.length - 1];
    let randomiseArray = [];
    if (randomise === "true") {
      randomiseArray.push(filterSections[0]);
      const sectionArray = filterSections.slice(1, filterSections.length - 1);
      const arrayIndex = [];
      do {
        const randomNumber = Math.floor(Math.random() * sectionArray.length) + 1;
        if (!arrayIndex.includes(randomNumber)) {
          arrayIndex.push(randomNumber);
          randomiseArray.push(sectionArray[randomNumber - 1]);
        }
      } while (arrayIndex.length < sectionArray.length);
      randomiseArray.push(filterSections[filterSections.length - 1]);
    } else {
      randomiseArray = filterSections;
    }
    return randomiseArray;
  };
  const quizContentSuccess = (template, sections) => {
    const filterSections = sections.filter(section => {
      var _section$answers;
      return section.title !== "" && ((_section$answers = section.answers) === null || _section$answers === void 0 ? void 0 : _section$answers.filter(answer => answer.text !== "").length) >= 2 && typeof section.correctAnswer !== "undefined" || !section.sectionName.includes("mc-quiz-question");
    });
    const sectionContent = randomiseQuestion(filterSections);
    setOnboardingMcQuizContent({
      ...INITIAL_STATE,
      template,
      sections: sectionContent
    });
  };
  const quizContentFailure = () => {
    setOnboardingMcQuizContent(INITIAL_STATE);
    addFailureNotification(t("Onboarding multiple choice quiz page contents missing, please contact Limitless support."));
  };
  const updateSelectedAnswer = (value, sectionIndex) => {
    const sections = clonedeep(onboardingMcQuizContent.sections);
    if (sectionIndex !== -1) {
      sections.find((item, index) => {
        if (sectionIndex === index) {
          if (Reflect.getOwnPropertyDescriptor(item, "wrongAnswer")) {
            item.wrongAnswer = false;
          }
          item.selectedAnswer = value;
        }
      });
      setOnboardingMcQuizContent({
        ...onboardingMcQuizContent,
        sections
      });
    }
  };
  const isAllowToSubmitAnswers = () => {
    var _onboardingMcQuizCont2;
    const totalQuizes = (_onboardingMcQuizCont2 = onboardingMcQuizContent.sections.filter(item => item.sectionName.includes("mc-quiz-question"))) === null || _onboardingMcQuizCont2 === void 0 ? void 0 : _onboardingMcQuizCont2.length;
    return onboardingMcQuizContent.sections.filter(item => Reflect.getOwnPropertyDescriptor(item, "selectedAnswer")).length !== totalQuizes || (onboardingMcQuizContent.sections.find(item => !!item.wrongAnswer) ? true : false);
  };
  const getTryAgainSuccessContent = (subSections, sectionName, scoreSuffix) => {
    const section = subSections.find(item => item.sectionName === sectionName);
    const sectionContent = section !== null && section !== void 0 ? section : defaultSectionContent(t)[sectionName];
    setQuizTryAgainSuccessContent({
      ...sectionContent,
      scoreSuffix
    });
  };
  const renderQuizQuestion = () => {
    const sections = randomiseQuestion(clonedeep(onboardingMcQuizContent.sections));
    setOnboardingMcQuizContent({
      ...onboardingMcQuizContent,
      sections
    });
  };
  const submitQuiz = () => {
    var _ctaContent$passMark, _quizes$filter;
    const {
      sections
    } = onboardingMcQuizContent;
    const quizes = sections.filter(item => item.sectionName.includes("mc-quiz-question"));
    const ctaContent = sections.find(item => item.sectionName === "quiz-cta");
    const passMark = (_ctaContent$passMark = ctaContent === null || ctaContent === void 0 ? void 0 : ctaContent.passMark) !== null && _ctaContent$passMark !== void 0 ? _ctaContent$passMark : quizes.length;
    const correctAnswerCount = (_quizes$filter = quizes.filter(item => item.selectedAnswer === item.correctAnswer)) === null || _quizes$filter === void 0 ? void 0 : _quizes$filter.length;
    const percentReceived = correctAnswerCount / quizes.length * 100;
    const percentRequired = passMark / quizes.length * 100;
    const quizResult = percentReceived >= percentRequired ? "pass" : "fail";
    dispatch({
      type: LOADING
    });
    updateQuizStatus(Math.round(percentReceived), response => {
      if (response) {
        dispatch({
          type: FINISH_QUIZ,
          payload: {
            ...response,
            loading: false,
            quizResult,
            percentReceived
          }
        });
      } else {
        dispatch({
          type: UPDATE_QUIZ,
          payload: {
            loading: false
          }
        });
      }
    });
  };
  const quizTryAgainReviewAnswers = () => {
    setQuizTryAgainSuccessContent({});
    wrongAnswers.forEach(item => item.wrongAnswer = true);
    const sections = randomiseQuestion(clonedeep(onboardingMcQuizContent.sections));
    setOnboardingMcQuizContent({
      ...onboardingMcQuizContent,
      sections
    });
  };
  const handleChangeOnboardingInvitationStatus = navigate => status => {
    changeOnboardingInvitationStatus(status, response => {
      if (status && (response === null || response === void 0 ? void 0 : response.result) === "success") {
        getOnboardingDetail(optInGroup);
        navigate("/onboarding/detail");
      } else {
        navigate("/onboarding");
      }
    });
  };
  const handleCloseDailog = () => {
    setMultipleChoiceQuizModalOpen(false);
    if (Object.values(quizTryAgainSuccessContent).includes("success-message")) {
      dispatch({
        type: CLOSE_QUIZ
      });
    }
  };
  const handleOptInCtaClick = navigate => status => {
    var _onboardingMcQuizCont3;
    initiateBrandLearning();
    if (onboardingBrandLearningContent.headerAndCtaSections.length > 0 && status) {
      setShowBrandLearningPage(true);
    } else if (((_onboardingMcQuizCont3 = onboardingMcQuizContent.sections) === null || _onboardingMcQuizCont3 === void 0 ? void 0 : _onboardingMcQuizCont3.length) > 0 && status) {
      setMultipleChoiceQuizModalOpen(true);
    } else {
      handleChangeOnboardingInvitationStatus(navigate)(status);
    }
  };
  const handleQuizTryAgainSuccessClick = sectionName => {
    if (sectionName === "success-message") {
      dispatch({
        type: CLOSE_QUIZ
      });
    } else if (sectionName === "try-again") {
      dispatch({
        type: TRY_AGAIN
      });
      quizTryAgainReviewAnswers();
    } else if (sectionName === "no-attempts-left-message") {
      setMultipleChoiceQuizModalOpen(false);
    }
  };
  const handleBrandLearningCtaClick = navigate => () => {
    var _onboardingMcQuizCont4;
    if (((_onboardingMcQuizCont4 = onboardingMcQuizContent.sections) === null || _onboardingMcQuizCont4 === void 0 ? void 0 : _onboardingMcQuizCont4.length) > 0) {
      setMultipleChoiceQuizModalOpen(true);
    } else {
      handleChangeOnboardingInvitationStatus(navigate)(true);
    }
  };
  return {
    onboardingMcQuizContent,
    updateSelectedAnswer,
    isAllowToSubmitAnswers,
    multipleChoiceQuizModalOpen,
    submitQuiz,
    renderQuizQuestion,
    showBrandLearningPage,
    quizState,
    randomiseQuestion,
    setQuizTryAgainSuccessContent,
    quizTryAgainSuccessContent,
    quizTryAgainReviewAnswers,
    handleQuizTryAgainSuccessClick,
    handleOptInCtaClick,
    handleCloseDailog,
    handleChangeOnboardingInvitationStatus,
    handleBrandLearningCtaClick
  };
};